import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Landmark } from 'lucide-react';
import { api } from '../../services/api';
import PageHeader from '../PageHeader';
import EmptyState from '../EmptyState';
import DataTable, { type CardSpec, type Column } from '../DataTable';
import { OrgName } from '../OrgLink';
import { apiErrorMessage } from '../sales/shared';
import { formatDate, formatHoursClock, formatNumber } from '../../utils/format';
import type { BankOverRow } from '../../types';

/** The usage tab of the bank's engagement (D-347 §1: a row opens the bank card, tab "שימוש"). */
export const bankRowHref = (r: BankOverRow) => `/engagements/${r.engagement_id}?tab=usage`;

const clock = (hours: number) => formatHoursClock(Math.round(Number(hours) * 60));

/**
 * D-347 §1 - /engagements?banks_over=1: the hour banks at or over their warning threshold
 * (`warning_threshold_pct`, 80 when none), most used first. The customer is the prominent column.
 * The server scopes the rows: the OTE manager sees every customer, an account manager his own.
 */
export default function BanksOverList() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['banks-over'],
    queryFn: () => api.getBanksOver().then((r) => (r.data ?? []) as BankOverRow[]),
  });
  const rows = data ?? [];

  const columns: Column<BankOverRow>[] = [
    {
      key: 'organization',
      header: t('banksOver.customer'),
      link: true,
      render: (r) => <span className="font-semibold"><OrgName orgId={r.organization_id} name={r.organization_name} /></span>,
    },
    { key: 'bank', header: t('banksOver.bank'), render: (r) => <bdi>{r.bank_name || '—'}</bdi> },
    { key: 'purchased', header: t('banksOver.purchased'), align: 'end', priority: 2, render: (r) => <span className="tabular-nums">{clock(r.purchased_hours)}</span> },
    { key: 'used', header: t('banksOver.used'), align: 'end', priority: 2, render: (r) => <span className="tabular-nums">{clock(r.used_hours)}</span> },
    { key: 'remaining', header: t('banksOver.remaining'), align: 'end', render: (r) => <span className={`tabular-nums ${r.remaining_hours < 0 ? 'font-medium text-danger-700' : ''}`}>{clock(r.remaining_hours)}</span> },
    { key: 'pct', header: t('banksOver.usagePct'), align: 'end', render: (r) => <span className="font-semibold tabular-nums text-warning-700">{formatNumber(r.usage_pct, 1)}%</span> },
    { key: 'expiry', header: t('banksOver.expiry'), align: 'end', priority: 2, render: (r) => <span className="tabular-nums text-text-2">{r.expiry_date ? formatDate(r.expiry_date) : '—'}</span> },
  ];

  const card = (r: BankOverRow): CardSpec => ({
    title: <bdi>{r.organization_name}</bdi>,
    badges: <span className="font-semibold tabular-nums text-warning-700">{formatNumber(r.usage_pct, 1)}%</span>,
    metaItems: [r.bank_name, `${t('banksOver.remaining')}: ${clock(r.remaining_hours)}`],
  });

  return (
    <div>
      <PageHeader
        title={t('banksOver.title')}
        description={t('banksOver.desc')}
        actions={<Link to="/engagements" className="btn btn-secondary">{t('banksOver.showAll')}</Link>}
      />
      <DataTable
        columns={columns}
        data={rows}
        keyField="bank_id"
        caption={t('banksOver.title')}
        mobile="cards"
        getRowHref={bankRowHref}
        rowLabel={(r) => `${r.organization_name} ${r.bank_name ?? ''}`}
        card={card}
        isLoading={isLoading}
        skeletonRows={6}
        error={isError ? error ?? true : undefined}
        errorMessage={isError ? apiErrorMessage(error, t('common.loadFailed')) : undefined}
        onRetry={() => { void refetch(); }}
        emptyState={<EmptyState icon={Landmark} title={t('banksOver.empty')} />}
      />
    </div>
  );
}
